import { computed, ref, SetupContext } from 'vue';
import { toasted } from '@/ui/helpers';
import { t } from '@/ui/i18n/i18n';

interface DragAndDropUploadProps {
  multiple: boolean;
  types: string[];
}

export function useDragAndDropUpload(props: DragAndDropUploadProps, ctx: SetupContext) {
  const fileInput = ref<HTMLAnchorElement|null>(null);
  const dragover = ref(false);
  const uploadedFiles = ref<File[]>([]);
  const uploadedFilenames = computed(() => uploadedFiles.value.map(file => file.name).join(', ') || '');

  const filterUploadedFiles = (files: FileList) => Array.from(files)
    .filter(file => !props.types || props.types.includes(file.type));

  const onBrowse = () => {
    fileInput.value?.click();
  };

  const onDeleteFiles = () => {
    uploadedFiles.value = [];

    ctx.emit('delete-files');
  };

  const validateUploadedFiles = (files: FileList, filteredFiles: File[]) => {
    if (filteredFiles.length !== Array.from(files).length) {
      toasted.error(t('upload.errors.unsupported-format'));

      return false;
    }

    if (!props.multiple && filteredFiles.length > 1) {
      toasted.error(t('upload.errors.only-single-file'));

      return false;
    }

    return true;
  };

  const onFileChange = (ev: Event) => {
    const input = ev.target as HTMLInputElement;

    if (!input?.files) {
      return;
    }

    const filteredFiles = filterUploadedFiles(input.files);
    if (!validateUploadedFiles(input.files, filteredFiles)) {
      return;
    }

    uploadedFiles.value = filteredFiles;
  };

  const onDrop = (ev: DragEvent) => {
    dragover.value = false;

    if (!ev.dataTransfer) {
      return;
    }

    const filteredFiles = filterUploadedFiles(ev.dataTransfer.files);
    if (!validateUploadedFiles(ev.dataTransfer.files, filteredFiles)) {
      return;
    }

    uploadedFiles.value = filteredFiles;
  };

  return {
    fileInput,
    dragover,
    uploadedFiles,
    uploadedFilenames,
    onBrowse,
    onDeleteFiles,
    onFileChange,
    onDrop,
  };
}
